import type {MRT_ColumnFiltersState, MRT_SortingState} from 'mantine-react-table';
import React from 'react';
import type {AttachmentClassificationResponse} from 'src/api/ProactApiClient';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {useGetAttachmentClassificationListQuery} from 'src/redux/queries/attachmentClassification';
import {useAttachmentClassificationColumns} from './hooks/useAttachmentClassificationColumns';

export interface AttachmentClassificationTableProps {
  identifier: string;
  defaultSorting?: MRT_SortingState;
  defaultColumnFilters?: MRT_ColumnFiltersState;
  onViewRowClick: (row: AttachmentClassificationResponse) => void;
  onCreate?: () => void;
}

export const AttachmentClassificationTable: React.FC<
  AttachmentClassificationTableProps
> = ({identifier, defaultSorting, defaultColumnFilters, onViewRowClick, onCreate}) => {
  const columns = useAttachmentClassificationColumns();

  const tableState = useTableState({
    identifier: identifier,
    columns: columns,
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: defaultSorting ?? [{id: 'name', desc: false}],
    defaultShowColumnFilters: false,
    defaultColumnFilters: defaultColumnFilters ?? [],
  });

  const {start, length} = tableState;

  const params = {
    start: start,
    length: length,
  };

  const {data, isLoading, isError, isFetching, refetch} =
    useGetAttachmentClassificationListQuery(params, {
      refetchOnMountOrArgChange: true,
    });

  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: false,
      manualFiltering: false,
      manualPagination: true,
      enableColumnFilters: false,
      columns: columns,
      data: rows,
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View onClick={() => onViewRowClick(row.original)} />
        </TableRowActions>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Actions>
            {onCreate && (
              <TableToolbar.Action variant="create" onClick={onCreate}>
                New Classification
              </TableToolbar.Action>
            )}
          </TableToolbar.Actions>
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return <>{table}</>;
};
